// src/pages/superadmin/CustomersManagement.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import { Select } from "../../arc/components/select/select";
import { Dialog, DialogContent } from "../../arc/components/dialog/dialog";
import { Input } from "../../arc/components/input/input";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";
import { roleLabel } from "../../config/roles";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }

  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN LAYOUT */
  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

  /* PANELS */
  .panel { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .panel-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; border-bottom: 1px solid var(--border); background: var(--surface-sub); gap: 12px; }
  .panel-title { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; display: flex; align-items: center; gap: 8px; }

  /* ALERTS */
  .alert-success { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: var(--green-dim); border: 1px solid var(--green-border); border-radius: var(--radius-sm); font-size: 13px; font-weight: 500; color: var(--green); margin-bottom: 16px; }
  .alert-error { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: var(--red-dim); border: 1px solid var(--red-border); border-radius: var(--radius-sm); font-size: 13px; font-weight: 500; color: var(--red); margin-bottom: 16px; }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--t), background var(--t), border-color var(--t), box-shadow var(--t), opacity var(--t); }
  .btn:focus-visible { outline: 2px solid var(--accent-mid); outline-offset: 2px; }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: var(--accent-mid); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-secondary:hover { background: var(--surface-sub); color: var(--text); border-color: var(--border-strong); }
  
  .btn-link { background: none; border: none; padding: 0; font-family: var(--mono); font-size: 11.5px; color: var(--accent); cursor: pointer; text-decoration: none; font-weight: 500; letter-spacing: 0.02em; transition: opacity var(--t); }
  .btn-link:hover { opacity: 0.65; }

  /* TABLE */
  .table-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { border-bottom: 1px solid var(--border); background: var(--surface-sub); }
  th { padding: 12px 16px; text-align: left; font-family: var(--mono); font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); font-weight: 500; white-space: nowrap; }
  tbody tr { border-bottom: 1px solid var(--border); background: var(--surface); transition: background var(--t); }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--surface-hover); }
  td { padding: 14px 16px; vertical-align: middle; font-size: 13px; }

  .cell-primary { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; margin-bottom: 2px; }
  .cell-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); }
  .cell-meta { font-family: var(--mono); font-size: 12px; color: var(--text-muted); }

  /* BADGES & TAGS */
  .badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 9px; border-radius: 100px; font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; }
  .badge-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .badge-active { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .badge-active .badge-dot { background: var(--green); }
  .badge-inactive { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .badge-inactive .badge-dot { background: var(--red); }

  .feature-tag { display: inline-flex; align-items: center; padding: 2px 8px; background: var(--surface-sub); border: 1px solid var(--border); border-radius: 4px; font-family: var(--mono); font-size: 10px; color: var(--text-muted); margin: 1px 2px 1px 0; letter-spacing: 0.02em; }
  .feature-tag.on { background: var(--accent-dim); border-color: var(--accent-glow); color: var(--accent); }

  /* LOADING */
  .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 24px; gap: 14px; }
  .spinner { width: 32px; height: 32px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); }

  /* MODAL */
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 200; }
  .modal-box { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-xl); max-width: 520px; width: 100%; padding: 28px; box-shadow: var(--shadow-modal); max-height: 90vh; overflow-y: auto; }
  .modal-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 24px; letter-spacing: -0.01em; }
  
  .form-group { margin-bottom: 16px; }
  .form-section { border-top: 1px solid var(--border); padding-top: 20px; margin-top: 20px; }
  .form-section-title { font-size: 12px; font-weight: 600; color: var(--text-muted); letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 14px; }
  
  .field-label, .form-group label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); margin-bottom: 6px; display: block; }
  .field-input { width: 100%; padding: 9px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .field-input::placeholder { color: var(--text-faint); }
  .field-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  select.field-input { cursor: pointer; -webkit-appearance: none; }
  
  .checkbox-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); cursor: pointer; transition: border-color var(--t); }
  .checkbox-row:hover { border-color: var(--border-strong); }
  .checkbox-row input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--accent); cursor: pointer; flex-shrink: 0; }
  .checkbox-label { font-size: 13px; color: var(--text-muted); font-weight: 500; user-select: none; }
  
  .modal-actions { display: flex; gap: 10px; margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--border); }
  .modal-actions .btn { flex: 1; }

  /* CREDENTIALS MODAL */
  .creds-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 6px; }
  .creds-subtitle { font-family: var(--mono); font-size: 12px; color: var(--text-muted); margin-bottom: 24px; }
  .creds-row { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
  .creds-key { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); }
  .creds-value { font-family: var(--mono); font-size: 13px; color: var(--text); background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 10px 14px; word-break: break-all; }
  .creds-warning { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--amber); background: var(--amber-dim); border: 1px solid var(--amber-border); border-radius: var(--radius-sm); padding: 12px 16px; margin-bottom: 24px; font-weight: 500; }
`;

const CustomerForm = ({
  onSubmit,
  submitLabel,
  formData,
  setFormData,
  showEditModal,
  setShowCreateModal,
  setShowEditModal: setShowEditModalProp,
  setSelectedCustomer,
  setError,
}) => (
  <form onSubmit={onSubmit}>
    <div className="form-group">
      <label className="field-label">Company Name</label>
      <Input
        label="Company Name"
        hideLabel
        type="text"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
        className="field-input"
        placeholder="Acme Corp"
      />
    </div>

    <div className="form-group">
      <Select
        label="Organisation Type"
        className="field-input"
        value={formData.organisationType || "customer"}
        onValueChange={(value) =>
          setFormData({ ...formData, organisationType: value })
        }
        options={[
          { value: "customer", label: "Customer (first user is Owner)" },
          {
            value: "partner",
            label: "External partner (first user is Partner)",
          },
        ]}
      />
    </div>

    {!showEditModal && (
      <div className="form-group">
        <label className="field-label">Slug (auto-generated if empty)</label>
        <Input
          label="Slug"
          hideLabel
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          className="field-input"
          placeholder="acme-corp"
        />
      </div>
    )}

    {showEditModal && (
      <div className="form-group">
        <Select
          label="License Status"
          className="field-input"
          value={formData.licenseStatus}
          onValueChange={(value) =>
            setFormData({ ...formData, licenseStatus: value })
          }
          options={[
            { value: "active", label: "Active" },
            { value: "suspended", label: "Suspended" },
            { value: "expired", label: "Expired" },
          ]}
        />
      </div>
    )}

    <div className="form-group">
      <label className="field-label">License Expiry Date (optional)</label>
      <Input
        label="License Expiry Date"
        hideLabel
        type="date"
        value={formData.licenseExpiresAt}
        onChange={(e) =>
          setFormData({ ...formData, licenseExpiresAt: e.target.value })
        }
        className="field-input"
      />
    </div>

    <div className="form-section">
      <div className="form-section-title">Features</div>
      <div className="form-group">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={formData.features.duda_enabled}
            onChange={(e) =>
              setFormData({
                ...formData,
                features: {
                  ...formData.features,
                  duda_enabled: e.target.checked,
                },
              })
            }
          />
          <span className="checkbox-label">
            Enable Wizzyl AI Website Generation
          </span>
        </label>
      </div>
      <div className="form-group">
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={!!formData.features.revenue_intelligence_enabled}
            onChange={(e) =>
              setFormData({
                ...formData,
                features: {
                  ...formData.features,
                  revenue_intelligence_enabled: e.target.checked,
                },
              })
            }
          />
          <span className="checkbox-label">
            Enable Revenue Intelligence (Phase 5, AI Sales Agent, Pipeline, AI
            Performance, Forecast)
          </span>
        </label>
      </div>
      <div className="form-group">
        <label className="field-label">Max Users</label>
        <Input
          label="Max Users"
          hideLabel
          type="number"
          value={formData.features.max_users}
          onChange={(e) =>
            setFormData({
              ...formData,
              features: {
                ...formData.features,
                max_users: parseInt(e.target.value),
              },
            })
          }
          min="1"
          className="field-input"
        />
      </div>
      <div className="form-group">
        <label className="field-label">Max Leads Per Month</label>
        <Input
          label="Max Leads Per Month"
          hideLabel
          type="number"
          value={formData.features.max_leads_per_month}
          onChange={(e) =>
            setFormData({
              ...formData,
              features: {
                ...formData.features,
                max_leads_per_month: parseInt(e.target.value),
              },
            })
          }
          min="1"
          className="field-input"
        />
      </div>
    </div>

    {!showEditModal && (
      <div className="form-section">
        <div className="form-section-title">Admin User</div>
        <div className="form-group">
          <label className="field-label">Full Name</label>
          <Input
            label="Full Name"
            hideLabel
            type="text"
            value={formData.adminUser.name}
            onChange={(e) =>
              setFormData({
                ...formData,
                adminUser: { ...formData.adminUser, name: e.target.value },
              })
            }
            required
            className="field-input"
            placeholder="Jane Smith"
          />
        </div>
        <div className="form-group">
          <label className="field-label">Email (optional)</label>
          <Input
            label="Email"
            hideLabel
            type="email"
            value={formData.adminUser.email}
            onChange={(e) =>
              setFormData({
                ...formData,
                adminUser: { ...formData.adminUser, email: e.target.value },
              })
            }
            className="field-input"
            placeholder="jane@acme.com"
          />
        </div>
        <div className="form-group">
          <label className="field-label">Username</label>
          <Input
            label="Username"
            hideLabel
            type="text"
            value={formData.adminUser.username}
            onChange={(e) =>
              setFormData({
                ...formData,
                adminUser: { ...formData.adminUser, username: e.target.value },
              })
            }
            required
            className="field-input"
            placeholder="jane.smith"
          />
        </div>
        <div className="form-group">
          <label className="field-label">Password</label>
          <Input
            label="Password"
            hideLabel
            type="password"
            value={formData.adminUser.password}
            onChange={(e) =>
              setFormData({
                ...formData,
                adminUser: { ...formData.adminUser, password: e.target.value },
              })
            }
            required
            className="field-input"
            placeholder="••••••••"
          />
        </div>
      </div>
    )}

    <div className="modal-actions">
      <button type="submit" className="btn btn-primary">
        {submitLabel}
      </button>
      <button
        type="button"
        onClick={() => {
          setShowCreateModal(false);
          setShowEditModalProp(false);
          setSelectedCustomer(null);
          setError("");
        }}
        className="btn btn-secondary"
      >
        Cancel
      </button>
    </div>
  </form>
);

const CustomersManagement = () => {
  const { user, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdUserInfo, setCreatedUserInfo] = useState(null);

  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const defaultFormData = {
    name: "",
    slug: "",
    organisationType: "customer",
    licenseExpiresAt: "",
    features: {
      duda_enabled: false,
      revenue_intelligence_enabled: false,
      max_users: 5,
      max_leads_per_month: 1000,
    },
    adminUser: {
      name: "",
      email: "",
      username: "",
      password: "",
    },
  };

  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCustomers();
      setCustomers(response.data);
    } catch (err) {
      console.error("Failed to load customers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await apiService.createCustomer({
        ...formData,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, "-"),
      });
      setShowCreateModal(false);
      setFormData(defaultFormData);
      setCreatedUserInfo(response.data.adminUser);
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create customer");
    }
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await apiService.updateCustomer(selectedCustomer.id, formData);
      setSuccess("Customer updated successfully!");
      setShowEditModal(false);
      setSelectedCustomer(null);
      loadCustomers();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update customer");
    }
  };

  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState("");
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");

  const closeRemoveModal = () => {
    if (removing) return;
    setRemoveTarget(null);
    setRemoveConfirm("");
    setRemoveError("");
  };

  const handleRemoveCustomer = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError("");
    try {
      const res = await apiService.removeCustomer(
        removeTarget.id,
        removeConfirm,
      );
      const hasSub = res?.data?.hasActiveSubscription;
      setSuccess(
        `${removeTarget.name} removed.` +
          (hasSub ? " Cancel their Stripe subscription separately." : ""),
      );
      setRemoving(false);
      setRemoveTarget(null);
      setRemoveConfirm("");
      loadCustomers();
      setTimeout(() => setSuccess(""), 6000);
    } catch (err) {
      setRemoving(false);
      setRemoveError(
        err.response?.data?.message || "Failed to remove customer",
      );
    }
  };

  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [linkError, setLinkError] = useState(null);

  const copyUniversalBriefLink = async (customer) => {
    setLinkError(null);
    try {
      const res = await apiService.generateUniversalBriefLink(customer.id);
      const url = res?.url || res?.data?.url;
      if (!url) throw new Error("No URL returned");
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(customer.id);
      setTimeout(
        () => setCopiedLinkId((id) => (id === customer.id ? null : id)),
        2000,
      );
    } catch (err) {
      setLinkError(customer.id);
      setTimeout(
        () => setLinkError((id) => (id === customer.id ? null : id)),
        2500,
      );
    }
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      slug: customer.slug,
      organisationType: customer.organisationType || "customer",
      licenseStatus: customer.licenseStatus,
      licenseExpiresAt: customer.licenseExpiresAt
        ? new Date(customer.licenseExpiresAt).toISOString().split("T")
        : "",
      features: customer.features,
    });
    setShowEditModal(true);
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`dash-root${darkMode ? " dark" : ""}`}>
        {/* HEADER */}
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to="/super-admin"
                className="btn-back"
                title="Back to Dashboard"
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Customers Management</div>
                <div className="dash-brand-sub">Axo Signal</div>
              </div>
            </div>

            <div className="dash-header-right">
              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn-header"
                  onClick={() => setSettingsOpen((o) => !o)}
                >
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
                <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) =>
                        applyThemeWithTransition(
                          next,
                          trigger,
                          setDarkMode,
                          variant,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn-header">
                Logout
              </button>
            </div>
          </div>
        </header>

        <div className="dash-main">
          {success && <div className="alert-success">✓ {success}</div>}

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">All Customers</div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn btn-primary"
              >
                Create Customer
              </button>
            </div>

            {loading ? (
              <div className="loading-state" style={{ width: "100%" }}>
                <Skeleton lines={6} label="Loading customers" />
              </div>
            ) : (
              <SortableDataTable
                caption="Customers"
                rowKey="id"
                defaultSort={{ key: "createdAt", direction: "desc" }}
                rows={customers.map((customer) => ({
                  ...customer,
                  _users: customer.users?.length || 0,
                }))}
                columns={[
                  {
                    key: "name",
                    label: "Customer",
                    render: (value, row) => (
                      <>
                        <div className="cell-primary">{value}</div>
                        <div className="cell-sub">{row.slug}</div>
                      </>
                    ),
                  },
                  {
                    key: "licenseStatus",
                    label: "License",
                    render: (value, row) => (
                      <>
                        <span
                          className={`badge ${value === "active" ? "badge-active" : "badge-inactive"}`}
                        >
                          <span className="badge-dot" />
                          {value}
                        </span>
                        {row.licenseExpiresAt && (
                          <div
                            className="cell-sub"
                            style={{ marginTop: "4px" }}
                          >
                            Expires:{" "}
                            {new Date(row.licenseExpiresAt).toLocaleDateString(
                              "en-GB",
                            )}
                          </div>
                        )}
                      </>
                    ),
                  },
                  {
                    key: "features",
                    label: "Features",
                    sortable: false,
                    render: (value) => (
                      <>
                        <span
                          className={`feature-tag${value.duda_enabled ? " on" : ""}`}
                        >
                          Duda {value.duda_enabled ? "✓" : "✗"}
                        </span>
                        <span
                          className={`feature-tag${value.revenue_intelligence_enabled ? " on" : ""}`}
                        >
                          Revenue Intel{" "}
                          {value.revenue_intelligence_enabled ? "✓" : "✗"}
                        </span>
                        <span className="feature-tag">
                          {value.max_users >= 999
                            ? "Unlimited seats"
                            : `${value.max_users} users`}
                        </span>
                      </>
                    ),
                  },
                  {
                    key: "_users",
                    label: "Users",
                    numeric: true,
                    render: (value) => <div className="cell-meta">{value}</div>,
                  },
                  {
                    key: "createdAt",
                    label: "Created",
                    render: (value) => (
                      <div className="cell-meta">
                        {new Date(value).toLocaleDateString("en-GB")}
                      </div>
                    ),
                  },
                  {
                    key: "_actions",
                    label: "",
                    sortable: false,
                    render: (_value, row) => (
                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={() => copyUniversalBriefLink(row)}
                          className="btn-link"
                          title="Copy a reusable brief-form link for this customer"
                        >
                          {copiedLinkId === row.id
                            ? "Copied ✓"
                            : linkError === row.id
                              ? "Failed, retry"
                              : "Copy brief link"}
                        </button>
                        <button
                          onClick={() => openEditModal(row)}
                          className="btn-link"
                        >
                          Edit →
                        </button>
                        {row.id !== "00000000-0000-0000-0000-000000000001" && (
                          <button
                            onClick={() => {
                              setRemoveTarget(row);
                              setRemoveConfirm("");
                              setRemoveError("");
                            }}
                            className="btn-link"
                            style={{ color: "var(--red)" }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) {
                setShowCreateModal(false);
                setError("");
              }
            }}
          >
            <DialogContent title="Create New Customer">
              {error && <div className="alert-error">✕ {error}</div>}
              <CustomerForm
                onSubmit={handleCreateCustomer}
                submitLabel="Create Customer"
                formData={formData}
                setFormData={setFormData}
                showEditModal={showEditModal}
                setShowCreateModal={setShowCreateModal}
                setShowEditModal={setShowEditModal}
                setSelectedCustomer={setSelectedCustomer}
                setError={setError}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Credentials Modal */}
        {createdUserInfo && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) setCreatedUserInfo(null);
            }}
          >
            <DialogContent
              title="Customer Created"
              description="Save these credentials, the password cannot be recovered later."
            >
              <div className="creds-warning">
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Share these details securely with the customer admin.
              </div>
              <div className="creds-row">
                <span className="creds-key">Name</span>
                <span className="creds-value">{createdUserInfo.name}</span>
              </div>
              {createdUserInfo.email && (
                <div className="creds-row">
                  <span className="creds-key">Email</span>
                  <span className="creds-value">{createdUserInfo.email}</span>
                </div>
              )}
              <div className="creds-row">
                <span className="creds-key">Username</span>
                <span className="creds-value">{createdUserInfo.username}</span>
              </div>
              <div className="creds-row">
                <span className="creds-key">Role</span>
                <span className="creds-value">
                  {roleLabel(createdUserInfo.role)}
                </span>
              </div>
              <div className="modal-actions" style={{ marginTop: "32px" }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setCreatedUserInfo(null)}
                >
                  Done
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Remove Modal */}
        {removeTarget && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) closeRemoveModal();
            }}
          >
            <DialogContent title="Remove customer?">
              {removeError && (
                <div className="alert-error">✕ {removeError}</div>
              )}
              <p style={{ fontSize: 13, lineHeight: 1.5, margin: "0 0 12px" }}>
                <strong>{removeTarget.name}</strong> will be hidden from this
                list, all its users will be signed out and unable to log in, and
                its API keys will be revoked. Payments, audit logs and lead
                history are kept. Any Stripe subscription is not cancelled
                automatically.
              </p>
              <Input
                label={`Type "${removeTarget.name}" to confirm`}
                value={removeConfirm}
                onChange={(e) => setRemoveConfirm(e.target.value)}
                disabled={removing}
                autoComplete="off"
              />
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 16,
                }}
              >
                <button
                  className="btn btn-secondary btn-md"
                  onClick={closeRemoveModal}
                  disabled={removing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-md"
                  style={{
                    background: "var(--red)",
                    color: "#fff",
                    border: "none",
                  }}
                  disabled={
                    removing || removeConfirm.trim() !== removeTarget.name
                  }
                  onClick={handleRemoveCustomer}
                >
                  {removing ? "Removing…" : "Remove customer"}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <Dialog
            open
            onOpenChange={(next) => {
              if (!next) {
                setShowEditModal(false);
                setSelectedCustomer(null);
                setError("");
              }
            }}
          >
            <DialogContent title="Edit Customer">
              {error && <div className="alert-error">✕ {error}</div>}
              <CustomerForm
                onSubmit={handleEditCustomer}
                submitLabel="Update Customer"
                formData={formData}
                setFormData={setFormData}
                showEditModal={showEditModal}
                setShowCreateModal={setShowCreateModal}
                setShowEditModal={setShowEditModal}
                setSelectedCustomer={setSelectedCustomer}
                setError={setError}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </>
  );
};

export default CustomersManagement;
